import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  api,
  type CasoDetalle,
  type Contacto,
  type RolCasoContacto,
} from "@/lib/api";
import { normalizeName } from "@/components/ContactoPicker";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";

type Props = {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
  caso: CasoDetalle;
};

type Mode = "elegir" | "crear";

/** Agrega un contacto del cliente al caso, con rol Principal o
 * Seguimiento. Dos modos:
 *  - Elegir existente: select con los contactos del cliente que aun
 *    no estan en el caso.
 *  - Crear nuevo: registra al contacto en el cliente (con dedup
 *    heuristico por nombre) y lo asigna al caso en un solo flujo.
 *
 * Si rol='principal', el principal anterior se demarca automaticamente.
 */
export default function AgregarContactoCasoModal({
  open,
  onClose,
  onAdded,
  caso,
}: Props) {
  const toast = useToast();
  const [contactos, setContactos] = useState<Contacto[]>([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<Mode>("elegir");
  const [contactoId, setContactoId] = useState<number | null>(null);
  const [regNombre, setRegNombre] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regTelefono, setRegTelefono] = useState("");
  const [rol, setRol] = useState<RolCasoContacto>("seguimiento");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setContactoId(null);
    setRol("seguimiento");
    setRegNombre("");
    setRegEmail("");
    setRegTelefono("");
    setMode("elegir");
    setLoading(true);
    api
      .get<Contacto[]>(`/api/clientes/${caso.cliente_id}/contactos`)
      .then(setContactos)
      .catch((err) => {
        const msg = err instanceof ApiError ? err.detail : "Error";
        toast.error("No pude cargar contactos del cliente", msg);
        setContactos([]);
      })
      .finally(() => setLoading(false));
    // `toast` cambia ref entre renders; si lo dejamos en deps dispara
    // el effect en bucle y termina spammeando toasts de error.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, caso.cliente_id]);

  // IDs ya asignados al caso, para no ofrecerlos en el select.
  const yaEnCaso = new Set(caso.contactos.map((c) => c.contacto_id));
  const disponibles = contactos.filter((c) => !yaEnCaso.has(c.id));

  // NOTA: antes auto-cambiabamos a modo "crear" cuando no habia
  // disponibles, pero confundia — el user esperaba ver primero la
  // lista de existentes. Ahora siempre arrancamos en elegir; si no
  // esta el que necesita, hace clic en "+ crear nuevo".

  // Sugerencias mientras el user escribe en modo crear: si hay un
  // contacto del cliente cuyo nombre se parece, lo ofrecemos para
  // evitar duplicados.
  const sugerencias = useMemo(() => {
    if (mode !== "crear") return [];
    const needle = normalizeName(regNombre);
    if (!needle || needle.length < 2) return [];
    return contactos
      .filter((c) => {
        const n = normalizeName(c.nombre);
        return n === needle || n.includes(needle) || needle.includes(n);
      })
      .slice(0, 3);
  }, [mode, regNombre, contactos]);

  function elegirSugerencia(c: Contacto) {
    setMode("elegir");
    setContactoId(c.id);
    setRegNombre("");
    setRegEmail("");
    setRegTelefono("");
    if (yaEnCaso.has(c.id)) {
      toast.error(`${c.nombre} ya está en el caso`);
    }
  }

  async function guardar() {
    setSaving(true);
    try {
      let idParaAsignar: number | null = contactoId;

      if (mode === "crear") {
        const nombre = regNombre.trim();
        if (!nombre) {
          toast.error("El nombre es obligatorio");
          setSaving(false);
          return;
        }
        // Dedup: si ya existe un contacto con ese nombre, lo reusamos.
        const needle = normalizeName(nombre);
        const exacto = contactos.find(
          (c) => normalizeName(c.nombre) === needle,
        );
        if (exacto) {
          idParaAsignar = exacto.id;
          if (yaEnCaso.has(exacto.id)) {
            toast.error(`${exacto.nombre} ya está en el caso`);
            setSaving(false);
            return;
          }
          toast.success("Contacto reutilizado", exacto.nombre);
        } else {
          const nuevo = await api.post<Contacto>(
            `/api/clientes/${caso.cliente_id}/contactos`,
            {
              nombre,
              email: regEmail.trim() || null,
              telefono: regTelefono.trim() || null,
            },
          );
          idParaAsignar = nuevo.id;
        }
      }

      if (idParaAsignar === null) {
        toast.error("Elige un contacto o crea uno nuevo");
        setSaving(false);
        return;
      }

      await api.post(`/api/casos/${caso.id}/contactos`, {
        contacto_id: idParaAsignar,
        rol,
      });
      toast.success("Contacto agregado al caso");
      onAdded();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude agregar el contacto", msg);
    } finally {
      setSaving(false);
    }
  }

  const disableGuardar =
    saving ||
    (mode === "elegir" && contactoId === null) ||
    (mode === "crear" && !regNombre.trim());

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="Agregar contacto al caso"
      description="Elige uno de los contactos del cliente o crea uno nuevo."
      size="md"
      footer={
        <>
          <button onClick={onClose} className="btn-ghost" disabled={saving}>
            Cancelar
          </button>
          <button
            onClick={guardar}
            disabled={disableGuardar}
            className="btn-primary disabled:opacity-50"
          >
            {saving ? "Agregando…" : "Agregar"}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs uppercase tracking-wide text-zinc-400">
              Contacto
            </label>
            {mode === "elegir" ? (
              <button
                onClick={() => setMode("crear")}
                className="text-xs text-accent-400 hover:text-accent-300 transition"
              >
                + crear nuevo
              </button>
            ) : (
              <button
                onClick={() => setMode("elegir")}
                className="text-xs text-accent-400 hover:text-accent-300 transition"
                disabled={disponibles.length === 0}
              >
                ← elegir existente
              </button>
            )}
          </div>

          {mode === "elegir" ? (
            <>
              <select
                className="input mt-1 w-full"
                value={contactoId ?? ""}
                onChange={(e) =>
                  setContactoId(e.target.value ? Number(e.target.value) : null)
                }
                disabled={loading}
              >
                <option value="">
                  {loading
                    ? "Cargando…"
                    : disponibles.length === 0
                    ? "— todos los contactos del cliente ya están en el caso —"
                    : "— elige un contacto —"}
                </option>
                {disponibles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                    {c.email ? ` (${c.email})` : ""}
                  </option>
                ))}
              </select>
              {!loading && disponibles.length === 0 && (
                <p className="text-xs text-zinc-500 mt-1">
                  Usa <span className="text-accent-400">+ crear nuevo</span>{" "}
                  arriba para registrarlo y asignarlo al caso en un solo paso.
                </p>
              )}
            </>
          ) : (
            <div className="space-y-2 mt-1">
              <input
                type="text"
                placeholder="Nombre *"
                value={regNombre}
                onChange={(e) => setRegNombre(e.target.value)}
                className="input w-full text-sm"
                autoFocus
              />
              {sugerencias.length > 0 && (
                <div className="rounded border border-amber-500/30 bg-amber-500/5 px-2 py-2 space-y-1">
                  <p className="text-[11px] text-amber-200">
                    {sugerencias.length === 1
                      ? "Ya existe un contacto similar — ¿es el mismo?"
                      : "Ya existen contactos similares — ¿alguno es el mismo?"}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {sugerencias.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => elegirSugerencia(c)}
                        disabled={yaEnCaso.has(c.id)}
                        className="text-xs px-2 py-1 rounded border border-amber-500/40 bg-amber-500/10 text-amber-100 hover:bg-amber-500/20 transition disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {c.nombre}
                        {c.email && (
                          <span className="text-amber-300/70 ml-1">
                            · {c.email}
                          </span>
                        )}
                        {yaEnCaso.has(c.id) && (
                          <span className="text-amber-300/50 ml-1">
                            (ya está en el caso)
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  type="email"
                  placeholder="Correo (opcional)"
                  value={regEmail}
                  onChange={(e) => setRegEmail(e.target.value)}
                  className="input w-full text-sm"
                />
                <input
                  type="text"
                  placeholder="Teléfono (opcional)"
                  value={regTelefono}
                  onChange={(e) => setRegTelefono(e.target.value)}
                  className="input w-full text-sm"
                />
              </div>
              <p className="text-xs text-zinc-500">
                Se registrará en la ficha del cliente y quedará asignado al
                caso en un solo paso.
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="text-xs uppercase tracking-wide text-zinc-400">
            Rol en el caso
          </label>
          <div className="mt-2 space-y-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="rol"
                value="seguimiento"
                checked={rol === "seguimiento"}
                onChange={() => setRol("seguimiento")}
                className="mt-1"
              />
              <span className="text-sm text-zinc-200">
                Seguimiento
                <span className="block text-xs text-zinc-500">
                  Va en CC al enviar correo cliente. Recibe el hilo pero
                  no es el destinatario principal.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="rol"
                value="principal"
                checked={rol === "principal"}
                onChange={() => setRol("principal")}
                className="mt-1"
              />
              <span className="text-sm text-zinc-200">
                Principal
                <span className="block text-xs text-zinc-500">
                  Es el destinatario directo del correo cliente (Para). Si
                  el caso ya tenía principal, ese pasa automáticamente a
                  seguimiento.
                </span>
              </span>
            </label>
          </div>
        </div>
      </div>
    </Modal>
  );
}
